import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import API_URL from '../config';
import { MAX_ATTACHMENTS, validateFileSelection } from '../utils/fileUploadValidation';
import ToastMessage from './ToastMessage';
import useToast from '../hooks/useToast';
import { useLanguage } from '../context/LanguageContext';
import { getStatusBadgeClass } from '../utils/statusBadge';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getApiErrorMessage, getAuthHeaders, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
import {
  addLocalDevSubtask,
  getLocalDevSubtasks,
  isLocalDevCompanyId,
  LOCAL_DEV_PROJECTS,
  LOCAL_DEV_USERS,
} from '../utils/localDevAuth';
const ManagerProjects = ({ user }) => {
  const { t, locale } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('ongoing');
  const [openProjects, setOpenProjects] = useState({});
  const [openMajorTasks, setOpenMajorTasks] = useState({});
  const [openSubtaskForms, setOpenSubtaskForms] = useState({});
  const [subtaskData, setSubtaskData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast, showToast, clearToast } = useToast();
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = user?.company?.id || localStorage.getItem('company_id') || '1';

  const buildLocalProjectsWithTasks = useCallback(() => {
    const localProjects = LOCAL_DEV_PROJECTS.filter(project => project.company.id === user.company?.id);
    const localTasks = getLocalDevSubtasks().filter(task => task.major_task?.project?.company?.id === user.company?.id);

    return localProjects.map(project => {
      const majorTasksById = new Map();
      localTasks
        .filter(task => task.major_task?.project?.id === project.id)
        .forEach(task => {
          const majorTask = task.major_task;
          const existingTask = majorTasksById.get(majorTask.id) || {
            ...majorTask,
            subtasks: [],
          };
          existingTask.subtasks = [...existingTask.subtasks, task];
          majorTasksById.set(majorTask.id, existingTask);
        });
      return { ...project, major_tasks: Array.from(majorTasksById.values()) };
    });
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const localProjectsWithTasks = buildLocalProjectsWithTasks();
        const localUsers = LOCAL_DEV_USERS.filter(localUser =>
          localUser.company.id === user.company?.id &&
          (localUser.role === 'team_member' || localUser.role === 'manager')
        );
        setProjects(localProjectsWithTasks);
        setUsers(localUsers);
        setFilteredUsers(localUsers);
        setError('');
        return;
      }
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const [projectsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/users/`, {
          headers: getAuthHeaders(token),
        }),
      ]);
const projectsWithTasks = await Promise.all(
        projectsRes.data.map(async (project) => {
let majorTasks = [];
          if (project.major_tasks && project.major_tasks.length > 0) {
            majorTasks = await Promise.all(
              project.major_tasks.map(async (taskId) => {
                try {
                  const taskRes = await axios.get(
                    `${API_URL}/api/major-tasks/${taskId}/`,
                    {
                      headers: getAuthHeaders(token),
                    }
                  );
const task = taskRes.data;
                  task.subtasks = task.subtasks || [];
return task;
                } catch (error) {
return null;
                }
              })
            );
            majorTasks = majorTasks.filter((task) => task !== null);
}
          return { ...project, major_tasks: majorTasks };
        })
      );
setProjects(projectsWithTasks);

      const initialFilteredUsers = usersRes.data.filter(u => {
        const isRoleMatch = u.role === 'team_member' || u.role === 'manager';
        const userCompanyId = user.company?.id || (typeof user.company === 'number' ? user.company : null);
        const uCompanyId = u.company?.id || (typeof u.company === 'number' ? u.company : null);
        const isCompanyMatch = uCompanyId && userCompanyId ? Number(uCompanyId) === Number(userCompanyId) : true;
return isRoleMatch && isCompanyMatch;
      });
setUsers(usersRes.data);
      setFilteredUsers(initialFilteredUsers);
      setError('');
    } catch (error) {
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: 'Failed to load data. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  }, [buildLocalProjectsWithTasks, effectiveCompanyId, isLocalDevSession, user, navigate]);

  useEffect(() => {
fetchData();
  }, [user, navigate, fetchData]);

  const toggleProject = (projectId) => {
    setOpenProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const toggleMajorTask = (taskId) => {
    setOpenMajorTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleSubtaskForm = (taskId) => {
    setOpenSubtaskForms(prev => ({ ...prev, [taskId]: !prev[taskId] }));
    if (!openSubtaskForms[taskId]) {
      setSubtaskData(prev => ({
        ...prev,
        [taskId]: {
          name: '',
          description: '',
          assigned_to_id: '',
          due_date: '',
          status: 'ongoing',
          priority: 'medium',
          assigned_files: [],
          assigned_links: [],
        },
      }));
      setFilteredUsers(users.filter(u => {
        const isRoleMatch = u.role === 'team_member' || u.role === 'manager';
        const userCompanyId = user.company?.id || (typeof user.company === 'number' ? user.company : null);
        const uCompanyId = u.company?.id || (typeof u.company === 'number' ? u.company : null);
        const isCompanyMatch = uCompanyId && userCompanyId ? Number(uCompanyId) === Number(userCompanyId) : true;
        return isRoleMatch && isCompanyMatch;
      }));
    }
  };

  const handleSubtaskChange = (taskId, field, value) => {
    setSubtaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const handleSubtaskFilesChange = (taskId, files) => {
    const incomingFiles = Array.from(files || []);
    setSubtaskData(prev => {
      const current = prev[taskId]?.assigned_files || [];
      const validation = validateFileSelection(current, incomingFiles);
      if (!validation.isValid) {
        showToast(validation.error, 'error');
        return prev;
      }
      setError('');
      return {
        ...prev,
        [taskId]: {
          ...prev[taskId],
          assigned_files: validation.files,
        },
      };
    });
  };

  const removeSubtaskFile = (taskId, index) => {
    setSubtaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        assigned_files: (prev[taskId]?.assigned_files || []).filter((_, i) => i !== index),
      },
    }));
  };

  const addSubtaskLink = (taskId) => {
    setSubtaskData(prev => {
      const current = prev[taskId]?.assigned_links || [];
      if (current.length >= MAX_ATTACHMENTS) {
        setError('Maximum 5 links allowed.');
        return prev;
      }
      setError('');
      return {
        ...prev,
        [taskId]: {
          ...prev[taskId],
          assigned_links: [...current, ''],
        },
      };
    });
  };

  const updateSubtaskLink = (taskId, index, value) => {
    setSubtaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        assigned_links: (prev[taskId]?.assigned_links || []).map((link, i) => (i === index ? value : link)),
      },
    }));
  };

  const removeSubtaskLink = (taskId, index) => {
    setSubtaskData(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        assigned_links: (prev[taskId]?.assigned_links || []).filter((_, i) => i !== index),
      },
    }));
  };

  const handleAssigneeSearch = (taskId, searchTerm) => {
    const filtered = users.filter(u => {
      const isRoleMatch = u.role === 'team_member' || u.role === 'manager';
      const userCompanyId = user.company?.id || (typeof user.company === 'number' ? user.company : null);
      const uCompanyId = u.company?.id || (typeof u.company === 'number' ? u.company : null);
      const isCompanyMatch = uCompanyId && userCompanyId ? Number(uCompanyId) === Number(userCompanyId) : true;
      const isEmailMatch = u.email.toLowerCase().includes(searchTerm.toLowerCase());
      return isRoleMatch && isCompanyMatch && isEmailMatch;
    });
    setFilteredUsers(filtered);
    handleSubtaskChange(taskId, 'assigned_to_id', '');
  };

  const handleSubtaskSubmit = async (taskId) => {
    try {
      if (isLocalDevSession) {
        const majorTask = projects
          .flatMap(project => project.major_tasks || [])
          .find(task => String(task.id) === String(taskId));
        const assignedTo = LOCAL_DEV_USERS.find(localUser =>
          String(localUser.id) === String(subtaskData[taskId]?.assigned_to_id)
        );
        if (!majorTask || !assignedTo || !subtaskData[taskId]?.name || !subtaskData[taskId]?.due_date) {
          setError('Please complete all required subtask fields.');
          return;
        }
        addLocalDevSubtask({
          majorTask,
          subtask: {
            ...subtaskData[taskId],
            due_datetime: `${subtaskData[taskId].due_date}T18:00:00`,
          },
          assignedTo,
          assignedBy: user,
        });
        toggleSubtaskForm(taskId);
        setSuccess('Subtask created successfully!');
        setError('');
        fetchData();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const payload = new FormData();
      payload.append('major_task', taskId);
      payload.append('name', subtaskData[taskId].name);
      payload.append('description', subtaskData[taskId].description || '');
      payload.append('assigned_to_id', subtaskData[taskId].assigned_to_id);
      payload.append('due_date', subtaskData[taskId].due_date || '');
      payload.append('status', subtaskData[taskId].status);
      payload.append('priority', subtaskData[taskId].priority);

      const assignedFiles = subtaskData[taskId]?.assigned_files || [];
      const assignedLinks = subtaskData[taskId]?.assigned_links || [];

      assignedFiles.forEach(file => payload.append('assigned_files', file));
      assignedLinks.filter(link => link).forEach(link => payload.append('assigned_links', link));

      await axios.post(`${API_URL}/api/subtasks/`, payload, {
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'multipart/form-data',
        },
      });

      toggleSubtaskForm(taskId);
      setSuccess('Subtask created successfully!');
      setError('');
      fetchData();
    } catch (error) {
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
const errorMessage = getApiErrorMessage(error, {
        fallback: 'Failed to create subtask.',
      });
      setError(errorMessage);
      setSuccess('');
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const ongoingProjects = filteredProjects.filter(p => ['ongoing', 'planned'].includes(p.status));
  const completedProjects = filteredProjects.filter(p => p.status === 'completed');
  const displayedProjects = activeTab === 'ongoing' ? ongoingProjects : completedProjects;
  const projectsToShow = showAllProjects ? displayedProjects : displayedProjects.slice(0, 10);

  if (loading) {
    return <p className="text-text italic text-sm sm:text-base">{t('common.loading')}</p>;
  }

  return (
    <>
      <ToastMessage toast={toast} onClose={clearToast} />
      <h1 className="text-2xl sm:text-3xl font-bold text-text mb-4 sm:mb-6">
        {t('common.projects')} - Welcome {user.code || 'Manager'}
      </h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
        <button
          className={`px-3 sm:px-4 py-1 sm:py-2 rounded-lg text-sm sm:text-base ${activeTab === 'ongoing' ? 'primary-btn text-white' : 'bg-gray-200 text-text hover:bg-hover-bg'}`}
          onClick={() => setActiveTab('ongoing')}
        >
          {t('manager.ongoingProjects')}
        </button>
        <button
          className={`px-3 sm:px-4 py-1 sm:py-2 rounded-lg text-sm sm:text-base ${activeTab === 'completed' ? 'primary-btn text-white' : 'bg-gray-200 text-text hover:bg-hover-bg'}`}
          onClick={() => setActiveTab('completed')}
        >
          {t('manager.completedProjects')}
        </button>
      </div>
      <div className="mb-4 sm:mb-6">
        <input
          type="text"
          placeholder="Search projects..."
          className="input-field w-full p-2 sm:p-3 text-text text-sm sm:text-base"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="mt-4 sm:mt-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">{activeTab === 'ongoing' ? t('manager.ongoingProjects') : t('manager.completedProjects')}</h2>
        {projectsToShow.length === 0 ? (
          <p className="text-text italic text-sm sm:text-base">No projects available.</p>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {projectsToShow.map(project => {
const majorTasks = Array.isArray(project.major_tasks) ? project.major_tasks : [];
              const isCompleted = project.status === 'completed';
              return (
                <div key={project.id} className="card p-3 sm:p-4">
                  <div
                    className={`flex justify-between items-center p-2 sm:p-3 rounded-t-lg ${isCompleted ? 'bg-gray-200 cursor-default pointer-events-none' : 'project-summary-bar'}`}
                    onClick={isCompleted ? undefined : () => toggleProject(project.id)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 flex-wrap flex-1 pr-2">
                      <p className="font-semibold text-sm sm:text-base break-words"><span className="project-summary-label">Title:</span> {project.name}</p>
                      <p className="text-sm sm:text-base break-all"><span className="project-summary-label">Manager:</span> {project.manager?.email || 'N/A'}</p>
                      <p className="text-sm sm:text-base"><span className="project-summary-label">Start Date:</span> {project.start_date}</p>
                      <p className="text-sm sm:text-base"><span className="project-summary-label">End Date:</span> {project.end_date}</p>
                    </div>
                    {!isCompleted && <FaChevronDown className={`shrink-0 text-primary transition-transform ${openProjects[project.id] ? 'rotate-180' : ''}`} size={14} />}
                  </div>
                  {openProjects[project.id] && (
                    <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4 p-3 sm:p-4 bg-hover-bg rounded-b-lg">
                      {majorTasks.length === 0 ? (
                        <p className="text-text italic text-sm sm:text-base">No major tasks for this project.</p>
                      ) : (
                        majorTasks.map(task => {
const highPriority = task.subtasks?.filter(s => s.priority === 'high')?.length || 0;
                          const mediumPriority = task.subtasks?.filter(s => s.priority === 'medium')?.length || 0;
                          const lowPriority = task.subtasks?.filter(s => s.priority === 'low')?.length || 0;
                          const completedSubtasks = task.subtasks?.filter(s => s.status === 'completed')?.length || 0;
                          const totalSubtasks = task.subtasks?.length || 0;
const majorTaskCompleted = isCompleted; // Inherit from project
                          return (
                            <div key={task.id} className="card p-3 sm:p-4">
                              <div
                                className={`flex justify-between items-center cursor-pointer ${majorTaskCompleted ? 'cursor-default pointer-events-none' : ''}`}
                                onClick={majorTaskCompleted ? undefined : () => toggleMajorTask(task.id)}
                              >
                                <div className="flex flex-wrap space-x-2">
                                  <div className="flex items-center">
                                    <span className="w-3 sm:w-4 h-3 sm:h-4 bg-red-500 rounded-full mr-1"></span>
                                    <span className="text-text text-sm sm:text-base">{highPriority}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="w-3 sm:w-4 h-3 sm:h-4 bg-orange-500 rounded-full mr-1"></span>
                                    <span className="text-text text-sm sm:text-base">{mediumPriority}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="w-3 sm:w-4 h-3 sm:h-4 bg-green-500 rounded-full mr-1"></span>
                                    <span className="text-text text-sm sm:text-base">{lowPriority}</span>
                                  </div>
                                </div>
                                {!majorTaskCompleted && <FaChevronDown className={`text-text transition-transform ${openMajorTasks[task.id] ? 'rotate-180' : ''} shrink-0`} size={14} />}
                              </div>
                              <p className="text-text font-semibold text-sm sm:text-base">{task.name}</p>
                              <p className="text-text text-xs sm:text-sm">Progress: {completedSubtasks}/{totalSubtasks}</p>
                              {openMajorTasks[task.id] && (
                                <div className="mt-3 sm:mt-4">
                                  <h4 className="text-base sm:text-lg font-semibold text-secondary-text mb-2 sm:mb-3">Details</h4>
                                  <p className="text-text text-sm sm:text-base"><strong>Created By:</strong> {task.created_by?.email || 'N/A'}</p>
                                  <p className="text-text text-sm sm:text-base"><strong>Created At:</strong> {task.created_at ? formatDateTime24(task.created_at, locale) : 'N/A'}</p>
                                  <p className="text-text text-sm sm:text-base"><strong>Due Date & Time:</strong> {task.due_date ? formatDateTime24(task.due_date, locale) : 'N/A'}</p>
                                  {task.description && (
                                    <p className="text-text text-sm sm:text-base"><strong>Description:</strong> {task.description}</p>
                                  )}
                                  <h4 className="text-base sm:text-lg font-semibold text-secondary-text mt-3 sm:mt-4 mb-2 sm:mb-3">Subtasks</h4>
                                  {task.subtasks && task.subtasks.length > 0 ? (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                                        <thead>
                                          <tr className="soft-table-head">
                                            <th className="p-2 sm:p-3">Title</th>
                                            <th className="p-2 sm:p-3">Assignee</th>
                                            <th className="p-2 sm:p-3">Due Date & Time</th>
                                            <th className="p-2 sm:p-3">Priority</th>
                                            <th className="p-2 sm:p-3">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {task.subtasks.map((subtask) => {
                                            const assigneeEmail = subtask.assigned_to && typeof subtask.assigned_to === 'object' && subtask.assigned_to.email
                                              ? subtask.assigned_to.email
                                              : 'N/A';
return (
                                              <tr key={subtask.id} className="border-b border-secondary table-row-hover">
                                                <td className="p-2 sm:p-3 text-text">{subtask.name || 'N/A'}</td>
                                                <td className="p-2 sm:p-3 text-text">{assigneeEmail}</td>
                                                <td className="p-2 sm:p-3 text-text">
                                                  {subtask.due_date
                                                    ? formatDateTime24(subtask.due_date, locale)
                                                    : 'N/A'}
                                                </td>
                                                <td className="p-2 sm:p-3 text-text">{subtask.priority || 'N/A'}</td>
                                                <td className="p-2 sm:p-3 text-text">
                                                  <span className={getStatusBadgeClass(subtask.status)}>
                                                    {subtask.status || 'N/A'}
                                                  </span>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <p className="text-text italic text-sm sm:text-base">No subtasks available.</p>
                                  )}
                                  {!majorTaskCompleted && (
                                    <button
                                      className="mt-3 sm:mt-4 primary-btn px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base"
                                      onClick={() => toggleSubtaskForm(task.id)}
                                    >
                                      <FaPlus className="inline mr-1 sm:mr-2" />
                                      {openSubtaskForms[task.id] ? 'Cancel' : 'Create Subtask'}
                                    </button>
                                  )}
                                  {openSubtaskForms[task.id] && !majorTaskCompleted && (
                                    <div className="mt-3 sm:mt-4 card p-3 sm:p-4">
                                      <h4 className="text-base sm:text-lg font-semibold text-secondary-text mb-2 sm:mb-3">Add New Subtask</h4>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-name-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Title</label>
                                        <input
                                          type="text"
                                          id={`subtask-name-${task.id}`}
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          value={subtaskData[task.id]?.name || ''}
                                          onChange={(e) => handleSubtaskChange(task.id, 'name', e.target.value)}
                                          required
                                        />
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-description-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Description (Optional)</label>
                                        <textarea
                                          id={`subtask-description-${task.id}`}
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          value={subtaskData[task.id]?.description || ''}
                                          onChange={(e) => handleSubtaskChange(task.id, 'description', e.target.value)}
                                        />
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-assignee-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Assignee</label>
                                        <input
                                          type="text"
                                          className="input-field w-full p-1 sm:p-2 text-text mb-1 sm:mb-2 text-sm sm:text-base"
                                          placeholder="Search assignees..."
                                          onChange={(e) => handleAssigneeSearch(task.id, e.target.value)}
                                        />
                                        <select
                                          id={`subtask-assignee-${task.id}`}
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          value={subtaskData[task.id]?.assigned_to_id || ''}
                                          onChange={(e) => handleSubtaskChange(task.id, 'assigned_to_id', e.target.value)}
                                          required
                                        >
                                          <option value="">Select Assignee</option>
                                          {filteredUsers.map(assignee => (
                                            <option key={assignee.id} value={assignee.id}>{assignee.email}</option>
                                          ))}
                                        </select>
                                        {filteredUsers.length === 0 && (
                                          <p className="text-red-500 mt-1 sm:mt-2 text-sm sm:text-base">No assignees available.</p>
                                        )}
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-due-date-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Due Date</label>
                                        <input
                                          type="date"
                                          id={`subtask-due-date-${task.id}`}
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          value={subtaskData[task.id]?.due_date || ''}
                                          onChange={(e) => handleSubtaskChange(task.id, 'due_date', e.target.value)}
                                          required
                                        />
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-priority-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Priority</label>
                                        <select
                                          id={`subtask-priority-${task.id}`}
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          value={subtaskData[task.id]?.priority || 'medium'}
                                          onChange={(e) => handleSubtaskChange(task.id, 'priority', e.target.value)}
                                          required
                                        >
                                          <option value="low">Low</option>
                                          <option value="medium">Medium</option>
                                          <option value="high">High</option>
                                        </select>
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <label htmlFor={`subtask-files-${task.id}`} className="block text-text mb-1 sm:mb-2 text-sm sm:text-base">Attach Files (Optional)</label>
                                        <input
                                          type="file"
                                          id={`subtask-files-${task.id}`}
                                          multiple
                                          className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                          onChange={(e) => handleSubtaskFilesChange(task.id, e.target.files)}
                                        />
                                        <p className="text-xs sm:text-sm text-secondary-text mt-1">Max 5 files, total 25MB.</p>
                                        {(subtaskData[task.id]?.assigned_files || []).length > 0 && (
                                          <ul className="mt-2 space-y-1 text-xs sm:text-sm text-text">
                                            {(subtaskData[task.id]?.assigned_files || []).map((file, index) => (
                                              <li key={`${file.name}-${index}`} className="flex items-center justify-between">
                                                <span className="truncate">{file.name}</span>
                                                <button
                                                  type="button"
                                                  onClick={() => removeSubtaskFile(task.id, index)}
                                                  className="text-xs text-error transition hover:opacity-80 sm:text-sm"
                                                >
                                                  Remove
                                                </button>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                      <div className="mb-2 sm:mb-3">
                                        <div className="flex items-center justify-between mb-1 sm:mb-2">
                                          <label className="block text-text text-sm sm:text-base">Attach Links (Optional)</label>
                                          <button
                                            type="button"
                                            onClick={() => addSubtaskLink(task.id)}
                                            className="text-xs text-accent-blue transition hover:opacity-80 sm:text-sm"
                                          >
                                            Add Link
                                          </button>
                                        </div>
                                        <p className="text-xs sm:text-sm text-secondary-text mb-2">Max 5 links.</p>
                                        {(subtaskData[task.id]?.assigned_links || []).length === 0 && (
                                          <p className="text-xs sm:text-sm text-text italic">No links added.</p>
                                        )}
                                        <div className="space-y-2">
                                          {(subtaskData[task.id]?.assigned_links || []).map((link, index) => (
                                            <div key={`subtask-link-${task.id}-${index}`} className="flex items-center space-x-2">
                                              <input
                                                type="url"
                                                className="input-field w-full p-1 sm:p-2 text-text text-sm sm:text-base"
                                                value={link}
                                                onChange={(e) => updateSubtaskLink(task.id, index, e.target.value)}
                                                placeholder="https://example.com"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => removeSubtaskLink(task.id, index)}
                                                className="text-xs text-error transition hover:opacity-80 sm:text-sm"
                                              >
                                                Remove
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        className="w-full primary-btn p-2 sm:p-3 text-sm sm:text-base"
                                        onClick={() => handleSubtaskSubmit(task.id)}
                                      >
                                        Submit Subtask
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {displayedProjects.length > 10 && (
              <button
                className="mt-3 sm:mt-4 primary-btn px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base"
                onClick={() => setShowAllProjects(!showAllProjects)}
              >
                {showAllProjects ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ManagerProjects;
